import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FileSearch, Search } from 'lucide-react';
import { Skeleton } from '../ui/skeleton';
import { useFileSearch } from '../../hooks/useFileSearch';
import { FileInfo } from '../../services/fileService';
import ChatFileFilters from './ChatFileFilters';
import ChatFileRow from './ChatFileRow';

interface ChatFileSearchPanelProps {
  onSelectFiles: (files: FileInfo[], allPanelFileIds: string[]) => void;
  selectedFileIds: string[];
}

/** Picks documents to attach to the next question. */
const ChatFileSearchPanel: React.FC<ChatFileSearchPanelProps> = ({
  onSelectFiles,
  selectedFileIds,
}) => {
  const { t, i18n } = useTranslation();
  const search = useFileSearch();
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set(selectedFileIds));

  useEffect(() => {
    searchInputRef.current?.focus();
  }, []);

  useEffect(() => {
    setSelectedIds(new Set(selectedFileIds));
  }, [selectedFileIds]);

  const handleToggle = (file: FileInfo) => {
    const next = new Set(selectedIds);
    if (next.has(file.id)) next.delete(file.id);
    else next.add(file.id);
    setSelectedIds(next);

    // Reported outside the state updater: StrictMode calls updaters twice, and
    // this would fire the parent's selection handler twice with it.
    const visible = search.files.filter((candidate) => next.has(candidate.id));
    // A file selected earlier may not be in the current (filtered) list; keep it
    // selected by passing a placeholder the parent fills in from what it knows.
    const offList = selectedFileIds
      .filter((id) => next.has(id) && !search.files.some((candidate) => candidate.id === id))
      .map((id) => ({
        id,
        name: 'Unknown',
        title: 'Unknown',
        path: '',
        type: 'document' as const,
      }));

    onSelectFiles([...visible, ...offList], search.files.map((candidate) => candidate.id));
  };

  const dateLocale = i18n.language === 'en' ? 'en-US' : 'ja-JP';

  return (
    <div className="revy-ui mb-1 flex max-h-[400px] w-[340px] flex-col overflow-hidden rounded-xl border bg-popover text-popover-foreground shadow-lg">
      <div className="relative border-b p-2.5">
        <Search
          className="pointer-events-none absolute left-5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
          strokeWidth={1.9}
        />
        <input
          ref={searchInputRef}
          type="text"
          value={search.query}
          onChange={(event) => search.setQuery(event.target.value)}
          placeholder={t('chat.fileSearchPlaceholder')}
          aria-label={t('chat.fileSearchPlaceholder')}
          className="h-9 w-full rounded-lg border bg-background pl-8 pr-2.5 text-sm outline-hidden transition-colors placeholder:text-muted-foreground focus:border-ring/60 focus:ring-2 focus:ring-ring/20"
        />
      </div>

      <ChatFileFilters
        metadata={search.metadata}
        filters={search.filters}
        hasActiveFilters={search.hasActiveFilters}
        onToggle={search.toggleFilter}
        onClear={search.clearFilters}
      />

      <div className="max-h-[250px] flex-1 overflow-y-auto overscroll-contain p-1">
        {search.isLoading ? (
          <div data-testid="file-search-loading" className="space-y-1 p-1">
            {[0, 1, 2, 3].map((row) => (
              <Skeleton key={row} className="h-10 w-full" />
            ))}
          </div>
        ) : search.files.length === 0 ? (
          <div className="flex flex-col items-center gap-2 px-4 py-8 text-center">
            <FileSearch className="size-6 text-muted-foreground" strokeWidth={1.5} />
            <p className="text-sm text-muted-foreground">
              {search.query || search.hasActiveFilters ? t('chat.noFilesFound') : t('chat.noFiles')}
            </p>
          </div>
        ) : (
          <ul>
            {search.files.map((file) => (
              <ChatFileRow
                key={file.id}
                file={file}
                selected={selectedIds.has(file.id)}
                dateLocale={dateLocale}
                onToggle={handleToggle}
              />
            ))}
          </ul>
        )}
      </div>

      <p className="border-t px-2.5 py-1.5 text-xs revy-figure text-muted-foreground">
        {t('chat.filesCount', { count: search.files.length })}
        {selectedIds.size > 0 && ` · ${t('chat.filesSelected', { count: selectedIds.size })}`}
      </p>
    </div>
  );
};

export default ChatFileSearchPanel;
